import React, { useState, useEffect } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Outlet,
  Navigate,
  useLocation,
} from 'react-router-dom';
import Dashboard from './Pages/Dashboard/Dashboard';
import Conversations from './Pages/Message/Conversations';
import Documents from './Pages/Documents/Documents';
import NotFound from './Pages/NotFound';
import Login from './Pages/Login/Login';
import ForgotPassword from './Pages/Login/ForgotPassword';
import ResetPassword from './Pages/Login/ResetPassword';
import ChangePassword from './Pages/Login/ChangePassword';
import Roster from './Pages/Roster/Roster';
import RosterDetail from './Pages/Roster/RosterDetail';
import Irs from './Pages/Irs/Irs';
import IrsDetail from './Pages/Irs/IrsDetail';
import Training from './Pages/Training/Training';
import TrainingCreate from './Pages/Training/TrainingCreate';
import ReleaseNotes from './Pages/Releases/ReleaseNotes';
import { Grid } from '@mui/material';
import Footer from './Footer';
import TrainingDetail from './Pages/Training/TrainingDetail';
import Donations from './Pages/Donations/public/Donations';
import UserList from './Pages/Users/UserList';
import { applications } from './utils/variables';
import LCChapterList from './Pages/Chapters/LocalChapter/LCChapterList';
import SPDChapterList from './Pages/Chapters/SPDChapter/SPDChapterList';
import BnBList from './Pages/BedAndBreakfast/BnBList';
import BnBCreate from './Pages/BedAndBreakfast/BnBCreate';
import DonationsList from './Pages/Donations/internal/DonationsList';
import ReciprocityDetail from './Pages/Chapters/Reciprocities/ReciprocityDetail';
import ReciprocityList from './Pages/Chapters/Reciprocities/ReciprocityList';
import TLCList from './Pages/TLC/TLCList';
import ELFLanding from './Pages/Projects/ELF';
import StarLandingPage from './Pages/Projects/Star';
import PCELandingPage from './Pages/Projects/PCE';
import PSALandingPage from './Pages/Projects/PSA';
import IPSLandingPage from './Pages/Projects/IPS';
import FoundationLandingPage from './Pages/Projects/Foundation';
import {
  MarketplaceList,
  MarketplaceDetail,
  MarketplaceCreate,
  MarketplaceEdit,
} from './Pages/Marketplace';
import NewsfeedList from './Pages/Newsfeed/NewsfeedList';
import NewsfeedDetail from './Pages/Newsfeed/NewsfeedDetail';
import NewsfeedCreate from './Pages/Newsfeed/NewsfeedCreate';
import NewsFeedView from './Pages/Newsfeed/NewsFeedView';
import DonationDetail from './Pages/Donations/internal/DonationDetail';
import UserDetail from './Pages/Users/UserDetail';
import IntroductionList from './Pages/Users/IntroOfWoman/IntroductionList';
import IntroductionDetail from './Pages/Users/IntroOfWoman/IntroductionDetail';
import IntroductionForm from './Pages/Users/IntroOfWoman/IntroductionForm';
import SideDrawer from './Drawer';
import { styled } from '@mui/material/styles';
import SPDChapterDetail from './Pages/Chapters/SPDChapter/SPDChapterDetail';
import LCDetail from './Pages/Chapters/LocalChapter/LCDetail';
import Report from './Pages/Reports/Report';
import { getMe } from './endpoints/login';
import { jwtDecode } from 'jwt-decode';
import { useApiGet } from './utils/httpClient';
import TLCDetail from './Pages/TLC/TLCDetail';
import BnBDetail from './Pages/BedAndBreakfast/BnBDetail';
import Cottey from './Pages/Projects/Cottey';
import IntlDetail from './Pages/Chapters/International/InternationalDetail';
import FeedbackCreate from './Pages/Feedback/ContactUs';
import SeekingTransferList from './Pages/Users/SeekingTransfer/SeekingTransferList';
import SeekingTransferDetail from './Pages/Users/SeekingTransfer/SeekingTransferDetail';
import SeekingTransferCreate from './Pages/Users/SeekingTransfer/SeekingTransferCreate';
import ConversationDetail from './Pages/Message/ConversationDetail';
import DocumentLibrary from './Pages/Library/DocumentLibrary';
import DocumentManager from './Pages/Library/DocumentManager';
import DocumentView from './Pages/Library/DocumentView';
import DocumentVersionDetail from './Pages/Library/DocumentVersionDetail';
import DocumentCreate from './Pages/Library/DocumentCreate';
import DocumentEdit from './Pages/Library/DocumentEdit';
import DocumentBundle from './Pages/Library/DocumentBundle';
import GroupPermissions from './Pages/Library/GroupPermissions';
import LeadershipDevelopment from './Pages/LDC/LeadershipDevelopment';
import Map from './Pages/Maps/Map';
import { config } from './utils/config';
import AnnualReportsDashboard from './Pages/AnnualReports/AnnualReportsDashboard';
import Prework from './Pages/AnnualReports/Prework';
import RosterDuesLanding from './Pages/AnnualReports/RosterDues/RosterDuesLanding';
import RosterDuesList from './Pages/AnnualReports/RosterDues/RosterDuesList';
import RosterDuesMemberList from './Pages/AnnualReports/RosterDues/RosterDuesMemberList';
import RosterDuesSummary from './Pages/AnnualReports/RosterDues/RosterDuesSummary';
import RosterDuesChapterList from './Pages/AnnualReports/RosterDues/RosterDuesChapterList';
import RosterDuesSPDSummary from './Pages/AnnualReports/RosterDues/RosterDuesSPDSummary';
import IRSFormsLanding from './Pages/AnnualReports/IRSForms/IRSFormsLanding';
import IRSFormsList from './Pages/AnnualReports/IRSForms/IRSFormsList';
import IRSFormsDetail from './Pages/AnnualReports/IRSForms/IRSFormsDetail';
import ARResources from './Pages/AnnualReports/Resources';
import ARTraining from './Pages/AnnualReports/Training';
import SPDPreworkForm from './Pages/AnnualReports/Prework/SPDPreworkForm';
import SPDPreworkList from './Pages/AnnualReports/Prework/SPDPreworkList';
import PEOReportsList from './Pages/PEOReports/PEOReportsList';
import PEOReportDetail from './Pages/PEOReports/PEOReportDetail';
import Permissions from './Pages/Administration/Permissions';
import ExecutiveOffice from './Pages/Administration/ExecutiveOffice';

const PrivateRoutes = () => {
  const location = useLocation();
  // Check auth in render to ensure it's reactive
  const auth = localStorage.getItem('user_id');

  if (!auth) {
    // Don't capture login page as returnUrl (would cause infinite loop)
    if (location.pathname === '/login') {
      return <Navigate to="/login" replace />;
    }

    // Save the intended destination before redirecting to login
    const returnUrl = location.pathname + location.search;
    return (
      <Navigate
        to={`/login?returnUrl=${encodeURIComponent(returnUrl)}`}
        replace
      />
    );
  }

  return <Outlet />;
};

const MessageAdmin = () => {
  let apps = localStorage.getItem('apps');
  if (!apps) return false;
  let messageUsers = [applications.MessageAdmin, applications.MessageApprover];
  return messageUsers.some((app) => apps.includes(app)) ? (
    <Outlet />
  ) : (
    <Navigate to="/" />
  );
};

const IsNewsfeedAdmin = () => {
  let apps = localStorage.getItem('apps');
  if (!apps) return false;
  let allowed = apps.includes(applications.NewsfeedAdmin);
  return allowed ? <Outlet /> : <Navigate to="/" />;
};

const IsDocumentAdmin = () => {
  let apps = localStorage.getItem('apps');
  if (!apps) return false;
  let allowed = apps.includes(applications.DocumentAdmin);
  return allowed ? <Outlet /> : <Navigate to="/" />;
};

const IsAnnualReportsUser = () => {
  let pages = localStorage.getItem('pages');
  if (!pages) return false;
  let allowed = pages.includes('annual-reports');
  return allowed ? <Outlet /> : <Navigate to="/" />;
};

const CanViewPermissions = () => {
  let pages = localStorage.getItem('pages');
  if (!pages) return false;
  let allowed = pages.includes('permissions');
  return allowed ? <Outlet /> : <Navigate to="/" />;
};

const CanViewExecutiveOffice = () => {
  let pages = localStorage.getItem('pages');
  if (!pages) return false;
  let allowed = pages.includes('exec-office');
  return allowed ? <Outlet /> : <Navigate to="/" />;
};

const drawerWidth = 300;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: 0,
    variants: [
      {
        props: ({ open }) => open,
        style: {
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
          }),
          marginLeft: `${drawerWidth}px`,
        },
      },
    ],
  })
);

const isMobile = window.innerWidth <= 768;

// Inner component that has access to router context
const RouterContent = () => {
  const location = useLocation();
  const [open, setOpen] = useState(true);
  const [enableGetMe, setEnableGetMe] = useState(false);

  // Only enable getMe after a short delay to ensure tokens are fully set after login
  // This prevents calling it immediately after login before tokens are ready
  useEffect(() => {
    const hasUserId = !!localStorage.getItem('user_id');
    const hasRefreshToken = !!localStorage.getItem('refresh');
    if (hasUserId && hasRefreshToken) {
      // Small delay to ensure tokens are ready after login
      const timer = setTimeout(() => {
        setEnableGetMe(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setEnableGetMe(false);
    }
  }, [location.pathname]); // Re-check when location changes (after login)

  const { data } = useApiGet(['refresh'], getMe, {
    enabled: enableGetMe,
    retry: 0,
    refetchInterval: 60 * 60000, // The data will be refetched every 60 minutes
  });

  // Make loggedIn reactive by checking localStorage in render
  // Re-renders when location changes (after login navigation)
  const loggedIn = !!localStorage.getItem('user_id');

  // Define public routes where drawer should not be shown
  const isPublicRoute =
    location.pathname === '/login' ||
    location.pathname === '/forgot-password' ||
    location.pathname.startsWith('/reset-password/');

  // Only show drawer if logged in AND not on a public route
  const shouldShowDrawer = loggedIn && !isPublicRoute;

  const handleUpdateDrawer = () => {
    setOpen(!open);
  };

  useEffect(() => {
    if (data) {
      try {
        localStorage.setItem('token', data.access_token);
        const decoded = jwtDecode(data.access_token);

        if (decoded.sub) {
          localStorage.setItem('user_id', decoded.sub);
        }
        if (decoded.first_name) {
          localStorage.setItem('first_name', decoded.first_name);
        }
        if (decoded.last_name) {
          localStorage.setItem('last_name', decoded.last_name);
        }
        if (decoded.applications?.length) {
          localStorage.setItem('apps', decoded.applications.join(','));
        }
        if (decoded.pages?.length) {
          localStorage.setItem('pages', decoded.pages.join(','));
        }
        if (decoded.role) {
          localStorage.setItem('role', decoded.role);
        }
      } catch (error) {
        console.error(
          'Error decoding token or writing to localStorage:',
          error
        );
        // Clear storage and redirect to login on error
        localStorage.clear();
        window.location.href = '/login';
      }
    }
  }, [data]);

  const routes = (
    <Routes>
      <Route element={<PrivateRoutes />}>
        <Route path="/roster" element={<Roster />} />
        <Route path="/roster/:id" element={<RosterDetail />} />
        <Route path="/irs" element={<Irs />} />
        <Route path="/reports" element={<Report />} />
        {config.NEW_REPORTS_ENABLED && (
          <>
            <Route path="/peo-reports" element={<PEOReportsList />} />
            <Route path="/peo-reports/:id" element={<PEOReportDetail />} />
          </>
        )}
        <Route path="/irs/:id" element={<IrsDetail />} />
        <Route path="/training" element={<Training />} />
        <Route path="/training/:id" element={<TrainingDetail />} />
        <Route path="/training/create" element={<TrainingCreate />} />
        <Route path="/documents" element={<Documents />} />
        <Route element={<IsDocumentAdmin />}>
          <Route path="/library/create" element={<DocumentCreate />} />
          <Route
            path="/library/:id/versions"
            element={<DocumentVersionDetail />}
          />
          <Route
            path="/library/:id/versions/:versionId"
            element={<DocumentVersionDetail />}
          />
          <Route path="/library/:id/edit" element={<DocumentEdit />} />
          <Route path="/library/manager" element={<DocumentManager />} />
          <Route
            path="/library/group-permissions"
            element={<GroupPermissions />}
          />
        </Route>
        <Route path="/library/:id/view/:versionId" element={<DocumentView />} />
        <Route path="/library/bundles/:bundleId" element={<DocumentBundle />} />
        <Route path="/library" element={<DocumentLibrary />} />
        <Route path="/release-notes" element={<ReleaseNotes />} />
        <Route path="/" element={<Dashboard />} />
        <Route path="/local-chapters" element={<LCChapterList />} />
        <Route path="/local-chapters/:id" element={<LCDetail />} />
        <Route path="/spd-chapters" element={<SPDChapterList />} />
        <Route path="/spd-chapters/:id" element={<SPDChapterDetail />} />
        <Route path="/international" element={<IntlDetail />} />
        <Route path="/bnb" element={<BnBList />} />
        <Route path="/bnb/create" element={<BnBCreate />} />
        <Route path="/bnb/:id" element={<BnBDetail />} />
        <Route path="/internal/donations" element={<DonationsList />} />
        <Route path="/internal/donations/:id" element={<DonationDetail />} />

        <Route
          path="/leadership-development"
          element={<LeadershipDevelopment />}
        />
        <Route path="/tlc" element={<TLCList />} />
        <Route path="/tlc/:id" element={<TLCDetail />} />
        <Route path="/user" element={<UserList />} />
        <Route path="/user/:id" element={<UserDetail />} />
        <Route path="/introductions" element={<IntroductionList />} />
        <Route path="/introductions/:id" element={<IntroductionDetail />} />
        <Route path="/introductions/create" element={<IntroductionForm />} />
        <Route path="/reciprocity" element={<ReciprocityList />} />
        <Route path="/reciprocity/:id" element={<ReciprocityDetail />} />
        <Route path="/projects/elf" element={<ELFLanding />} />
        <Route path="/projects/star" element={<StarLandingPage />} />
        <Route path="/projects/pce" element={<PCELandingPage />} />
        <Route path="/projects/psa" element={<PSALandingPage />} />
        <Route path="/projects/ips" element={<IPSLandingPage />} />
        <Route element={<IsNewsfeedAdmin />}>
          <Route path="/newsfeed" element={<NewsfeedList />} />
          <Route path="/newsfeed/:id" element={<NewsfeedDetail />} />
          <Route path="/newsfeed/create" element={<NewsfeedCreate />} />
        </Route>
        <Route path="/newsfeed/view/:id" element={<NewsFeedView />} />
        {config.SEEKING_TRANSFER_ENABLED && (
          <>
            <Route path="/seeking-transfer" element={<SeekingTransferList />} />
            <Route
              path="/seeking-transfer/:id"
              element={<SeekingTransferDetail />}
            />
            <Route
              path="/seeking-transfer/create/:userId"
              element={<SeekingTransferCreate />}
            />
          </>
        )}
        <Route path="/foundation" element={<FoundationLandingPage />} />
        <Route path="/cottey" element={<Cottey />} />
        <Route path="/marketplace" element={<MarketplaceList />} />
        <Route path="/marketplace/:id" element={<MarketplaceDetail />} />
        <Route path="/marketplace/create" element={<MarketplaceCreate />} />
        <Route path="/marketplace/:id/edit" element={<MarketplaceEdit />} />
        <Route element={<MessageAdmin />}></Route>
        <Route path="/conversations" element={<Conversations />} />
        <Route path="/conversations/:id" element={<ConversationDetail />} />
        <Route path="/feedback/create" element={<FeedbackCreate />} />
        <Route path="/map" element={<Map />} />

        <Route element={<IsAnnualReportsUser />}>
          <Route path="/annual-reports" element={<AnnualReportsDashboard />} />
          <Route path="/annual-reports/prework" element={<Prework />} />
          <Route
            path="/annual-reports/prework/spd"
            element={<SPDPreworkList />}
          />
          <Route path="/prework/spd/:id" element={<SPDPreworkForm />} />
          <Route
            path="/annual-reports/prework/spd/:id"
            element={<SPDPreworkForm />}
          />
          <Route
            path="/annual-reports/prework/spd-dues/:id"
            element={<SPDPreworkForm />}
          />
          <Route
            path="/annual-reports/roster-dues"
            element={<RosterDuesLanding />}
          />
          <Route
            path="/annual-reports/roster-dues/local-chapters"
            element={<RosterDuesList formType="local_chapter" />}
          />
          <Route
            path="/annual-reports/roster-dues/spd"
            element={<RosterDuesList formType="spd" />}
          />
          <Route
            path="/annual-reports/roster-dues/:id/members"
            element={<RosterDuesMemberList />}
          />
          <Route
            path="/annual-reports/roster-dues/:id/chapters"
            element={<RosterDuesChapterList />}
          />
          <Route
            path="/annual-reports/roster-dues/:id/summary"
            element={<RosterDuesSummary />}
          />
          <Route
            path="/annual-reports/roster-dues/:id/spd-summary"
            element={<RosterDuesSPDSummary />}
          />
          <Route
            path="/annual-reports/irs-forms"
            element={<IRSFormsLanding />}
          />
          <Route
            path="/annual-reports/irs-forms/:year/local-chapters"
            element={<IRSFormsList formType="lc" />}
          />
          <Route
            path="/annual-reports/irs-forms/:year/spd"
            element={<IRSFormsList formType="sc" />}
          />
          <Route
            path="/annual-reports/irs-forms/:year/reciprocity"
            element={<IRSFormsList formType="rg" />}
          />
          <Route
            path="/annual-reports/irs-forms/:year/local-chapters/:id"
            element={<IRSFormsDetail />}
          />
          <Route
            path="/annual-reports/irs-forms/:year/spd/:id"
            element={<IRSFormsDetail />}
          />
          <Route
            path="/annual-reports/irs-forms/:year/reciprocity/:id"
            element={<IRSFormsDetail />}
          />
          <Route path="/annual-reports/resources" element={<ARResources />} />
          <Route path="/annual-reports/training" element={<ARTraining />} />
        </Route>
        <Route element={<CanViewPermissions />}>
          <Route path="/administration/permissions" element={<Permissions />} />
        </Route>
        <Route element={<CanViewExecutiveOffice />}>
          <Route
            path="/administration/executive-office"
            element={<ExecutiveOffice />}
          />
        </Route>
      </Route>
      <Route path="*" element={<NotFound />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/change-password" element={<ChangePassword />} />
      <Route path="/donations" element={<Donations />} />
    </Routes>
  );
  return (
    <Grid sx={{ position: 'relative', minHeight: '100vh' }}>
      {shouldShowDrawer && (
        <SideDrawer
          open={open}
          isMobile={isMobile}
          handleUpdateDrawer={handleUpdateDrawer}
          drawerWidth={drawerWidth}
        />
      )}
      {isMobile ? (
        <Grid item xs={12} paddingBottom={12}>
          {routes}
        </Grid>
      ) : (
        <Grid item xs={12} paddingBottom={11}>
          {shouldShowDrawer ? (
            <Main open={open}>{routes}</Main>
          ) : (
            <React.Fragment>{routes}</React.Fragment>
          )}
        </Grid>
      )}
    </Grid>
  );
};

const Router = () => {
  return (
    <BrowserRouter>
      <RouterContent />
      <RouterContentFooter />
    </BrowserRouter>
  );
};

const RouterContentFooter = () => {
  // Check localStorage in render - will update when location changes (after login)
  const loggedIn = !!localStorage.getItem('user_id');

  if (!loggedIn) return null;

  return (
    <Grid item xs={12}>
      <Footer />
    </Grid>
  );
};

export default Router;
